import Stripe from "stripe";
import { bugNotif } from "../../api/bugsnag";
import { ViewWLSubscriptionsTiers } from "../../../Common/models/subscriptions";
import { getErrorMsg } from "../../../Common/utils/common";
import {
  generateDedupeId,
  trackApplovinConversionEvent,
  trackAxonEvent,
} from "./axon";
import {
  identifyKlaviyoUserEmail,
  identifyKlaviyoCompleteProfile,
  viewProductKlaviyoEvent,
  startCheckoutKlaviyoEvent,
  addtoCartKlaviyoEvent,
  completePurchaseSubscriptionKlaviyoEvent,
} from "./klaviyo";
import { sendgtag } from "./google";
import {
  sendFBTracking,
  sendFbqCart,
  sendFbqInitiateCheckout,
  sendFbqLead,
  sendFbqAddPaymentInfo,
  sendFbqCompleteRegistration,
} from "./facebook";
import {
  sendTTQViewEvent,
  sendTTQCheckoutEvent,
  sendTTQCompletedPurchaseEvent,
  sendttqTracking,
  sendttqAddToCart,
} from "./tiktok";
import { sendSnapTracking, sendSnapAddToCart } from "./snapchat";
import {
  trackTaboolaViewContent,
  trackTaboolaAddToCart,
  trackTaboolaStartCheckout,
  trackTaboolaCompleteRegistration,
} from "./taboola";

import { ENVIRONMENTS } from "../../../Common/config";

const isProductionEnv = () => import.meta.env.MODE === ENVIRONMENTS.production;

// Main tracking functions
export interface TrackSignupViewParams {
  emailAddress: string;
  subscription: ViewWLSubscriptionsTiers;
  skipKlaviyoViewProduct?: boolean; // Skip Klaviyo ViewProduct if already fired (e.g. from phone blur)
}

export const trackSignupProductView = async ({
  emailAddress,
  subscription,
  skipKlaviyoViewProduct = false,
}: TrackSignupViewParams): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  try {
    const dedupeId = generateDedupeId();

    if (isProductionEnv()) {
      const tasks: Promise<unknown>[] = [
        identifyKlaviyoUserEmail(emailAddress),
        sendTTQViewEvent(emailAddress, subscription),
        trackTaboolaViewContent(),
      ];
      if (!skipKlaviyoViewProduct) {
        tasks.push(viewProductKlaviyoEvent(subscription.docId));
      }
      await Promise.all(tasks);
    }

    const axonPayload = {
      currency: "AUD",
      value: subscription.price,
      items: [
        {
          item_id: subscription.docId,
          item_name: subscription.name,
          item_category: "Subscription",
          price: subscription.price,
          quantity: 1,
        },
      ],
    };

    trackAxonEvent("view_item", axonPayload, dedupeId);
    void trackApplovinConversionEvent(
      "view_item",
      axonPayload,
      { email: emailAddress },
      dedupeId
    );
  } catch (error) {
    console.log("error trackSignupProductView", error);
    bugNotif("trackSignupProductView", getErrorMsg(error).rawErrorMsg);
  }
};

export interface TrackSignupCheckoutParams {
  emailAddress: string;
  phoneNumber: string;
  subscription: ViewWLSubscriptionsTiers;
  tiktokSource?: string | null;
  validStripeCoupon?: Stripe.Coupon | null;
}

export const trackSignupCheckout = async ({
  emailAddress,
  phoneNumber,
  subscription,
  tiktokSource = null,
  validStripeCoupon = null,
}: TrackSignupCheckoutParams): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  try {
    const dedupeId = generateDedupeId();

    if (isProductionEnv()) {
      await Promise.all([
        sendTTQCheckoutEvent(
          emailAddress,
          phoneNumber,
          subscription,
          tiktokSource,
          validStripeCoupon
        ),
        startCheckoutKlaviyoEvent(subscription.docId, subscription.price),
        trackTaboolaStartCheckout(subscription.price),
      ]);
    }

    const axonPayload = {
      currency: "AUD",
      value: subscription.price,
      payment_type: "credit_card",
      items: [
        {
          item_id: subscription.docId,
          item_name: subscription.name,
          price: subscription.price,
          quantity: 1,
          item_category: "Subscription",
        },
      ],
    };

    trackAxonEvent("begin_checkout", axonPayload, dedupeId);
    void trackApplovinConversionEvent(
      "begin_checkout",
      axonPayload,
      { email: emailAddress, phone: phoneNumber },
      dedupeId
    );
  } catch (error) {
    console.log("error trackSignupCheckout", error);
    bugNotif("trackSignupCheckout", getErrorMsg(error).rawErrorMsg);
  }
};

export type SignupEventType = "Membership" | "One Off" | "Power Up";

export interface TrackSignupAddToCartParams {
  subscriptionId: string;
  itemName?: string;
  value: number;
  type: SignupEventType;
  emailAddress?: string | null;
}

export const trackSignupAddToCart = async ({
  subscriptionId,
  itemName,
  value,
  type,
  emailAddress,
}: TrackSignupAddToCartParams): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  try {
    if (isProductionEnv()) {
      const tasks: Promise<unknown>[] = [
        sendFbqCart(type, subscriptionId, value),
        addtoCartKlaviyoEvent(subscriptionId, value),
        trackTaboolaAddToCart(value),
      ];

      if (emailAddress) {
        tasks.push(
          sendSnapAddToCart(emailAddress, type, subscriptionId, value),
          sendttqAddToCart(emailAddress, subscriptionId, value)
        );
      }

      await Promise.all(tasks);
    }

    const dedupeId = generateDedupeId();
    const axonPayload = {
      currency: "AUD",
      value,
      items: [
        {
          item_id: subscriptionId,
          item_name: itemName ?? subscriptionId,
          price: value,
          quantity: 1,
          item_category: type,
        },
      ],
    };

    trackAxonEvent("add_to_cart", axonPayload, dedupeId);
    void trackApplovinConversionEvent(
      "add_to_cart",
      axonPayload,
      emailAddress ? { email: emailAddress } : {},
      dedupeId
    );
  } catch (error) {
    console.log("error trackSignupAddToCart", error);
    bugNotif("trackSignupAddToCart", getErrorMsg(error).rawErrorMsg);
  }
};

export interface TrackSignupPurchaseParams {
  userId: string;
  subscriptionId: string;
  value: number;
  type: SignupEventType;
  transactionId?: string;
  tiktokSource?: string | null;
  itemName?: string | null;
  emailAddress?: string | null;
  phoneNumber?: string | null;
}

const toApplovinPhoneDigits = (phoneNumber: string): string =>
  phoneNumber.replace(/\D/g, "");

export const trackSignupPurchaseCompletion = async ({
  userId,
  subscriptionId,
  value,
  type,
  transactionId,
  tiktokSource = null,
  itemName = null,
  emailAddress = null,
  phoneNumber = null,
}: TrackSignupPurchaseParams): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  try {
    let eventId: string | undefined;
    if (isProductionEnv()) {
      eventId = await sendttqTracking(userId, subscriptionId, value);

      await Promise.all([
        sendgtag(transactionId ?? subscriptionId, value),
        sendSnapTracking(userId, type, subscriptionId, value),
        sendFBTracking(type, subscriptionId, value),
        completePurchaseSubscriptionKlaviyoEvent(subscriptionId, value),
        sendTTQCompletedPurchaseEvent(
          userId,
          value,
          subscriptionId,
          tiktokSource,
          eventId
        ),
        trackTaboolaCompleteRegistration(value),
      ]);
    }

    const purchaseDedupeId = transactionId ?? subscriptionId;
    const email = emailAddress?.trim().toLowerCase() || undefined;
    const phone = phoneNumber
      ? toApplovinPhoneDigits(phoneNumber) || undefined
      : undefined;
    const userData = {
      user_id: userId,
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
    };
    const axonPayload = {
      currency: "AUD",
      value,
      payment_type: "credit_card",
      event_id: eventId,
      transaction_id: purchaseDedupeId,
      items: [
        {
          item_id: subscriptionId,
          item_name: itemName ?? subscriptionId,
          price: value,
          quantity: 1,
          item_category: type,
        },
      ],
      user_data: userData,
    };

    trackAxonEvent("purchase", axonPayload, purchaseDedupeId);
    void trackApplovinConversionEvent(
      "purchase",
      axonPayload,
      userData,
      purchaseDedupeId
    );
  } catch (error) {
    console.log("error trackSignupPurchaseCompletion", error);
    bugNotif("trackSignupPurchaseCompletion", getErrorMsg(error).rawErrorMsg);
  }
};

export interface TrackSignupInitiateCheckoutParams {
  subscription: ViewWLSubscriptionsTiers;
}

export const trackSignupInitiateCheckout = async ({
  subscription,
}: TrackSignupInitiateCheckoutParams): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  try {
    await sendFbqInitiateCheckout(
      subscription.name,
      subscription.price,
      subscription.docId
    );
  } catch (error) {
    console.log("error trackSignupInitiateCheckout", error);
    bugNotif("trackSignupInitiateCheckout", getErrorMsg(error).rawErrorMsg);
  }
};

export const trackSignupLead = async (): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  try {
    await sendFbqLead();
  } catch (error) {
    console.log("error trackSignupLead", error);
    bugNotif("trackSignupLead", getErrorMsg(error).rawErrorMsg);
  }
};

export interface TrackSignupAddPaymentInfoParams {
  subscription: ViewWLSubscriptionsTiers;
}

export const trackSignupAddPaymentInfo = async ({
  subscription,
}: TrackSignupAddPaymentInfoParams): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  try {
    await sendFbqAddPaymentInfo(
      subscription.name,
      subscription.price,
      subscription.docId
    );
  } catch (error) {
    console.log("error trackSignupAddPaymentInfo", error);
    bugNotif("trackSignupAddPaymentInfo", getErrorMsg(error).rawErrorMsg);
  }
};

export interface TrackSignupCompleteRegistrationParams {
  subscriptionId: string;
  value: number;
  itemName?: string | null;
}

export const trackSignupCompleteRegistration = async ({
  subscriptionId,
  value,
  itemName = null,
}: TrackSignupCompleteRegistrationParams): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  try {
    await sendFbqCompleteRegistration(
      itemName ?? subscriptionId,
      value,
      subscriptionId
    );
  } catch (error) {
    console.log("error trackSignupCompleteRegistration", error);
    bugNotif(
      "trackSignupCompleteRegistration",
      getErrorMsg(error).rawErrorMsg
    );
  }
};

// Export identifyKlaviyoCompleteProfile and identifyKlaviyoUserPhone as they're used directly by components
export {
  identifyKlaviyoCompleteProfile,
  identifyKlaviyoUserPhone,
  viewProductKlaviyoEvent,
} from "./klaviyo";
